import { type FunctionBasedModifier, modifier } from "ember-modifier";

export type PreserveMode = "aspect-ratio" | "height";

export interface BalanceRowsSignature {
  Args: {
    Positional: [];
    Named: {
      /** See PreserveMode. Defaults to "aspect-ratio". */
      preserve?: PreserveMode;
      /** Redistribute rows evenly and justify the last row. Defaults to false. */
      aggressive?: boolean;
      /**
       * CSS selector for the item elements to lay out, so the modifier works
       * on your own markup (`<div>`s and so on), not just `<li>`. Defaults to
       * "li". Matched against the container's direct children only, so a
       * matching element nested inside an item's content is left alone. The
       * modifier's own internal sentinel element is always excluded.
       */
      selector?: string;
    };
  };
  Element: HTMLElement;
}

// when the last row would stretch to more than 150% the tallest balanced
// row above it, we leave it at natural size instead of filling
const LAST_ROW_MAX_HEIGHT_RATIO = 1.5;
// in height-preserve mode we use a natural-width fill threshold instead of
// a height ratio: stretch the last row only when it's already close to full
const LAST_ROW_FILL_THRESHOLD = 0.95;
// subtracted from the summed percentage widths so floating-point rounding
// inside the browser's layout engine can never push the row total past
// 100% (which would wrap the last item).  Expressed in px so it survives
// percentage resolution unchanged.
const ROW_SAFETY_MARGIN_PX = 1;

type RowLayout = {
  row: HTMLElement[];
  totalNaturalWidth: number;
  totalGap: number;
  scale: number;
  scaledHeight: number;
};

function measureContainer(container: HTMLElement): {
  containerWidth: number;
  gapWidth: number;
} {
  const style = getComputedStyle(container);

  return {
    // clientWidth includes padding but items lay out inside the padding box,
    // so subtract horizontal padding to get the width available to items
    containerWidth:
      container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
    gapWidth: style.gap ? parseInt(style.gap) : 8,
  };
}

function resetItemDimensions(items: HTMLElement[]): void {
  items.forEach((item) => {
    item.style.width = "";
    item.style.height = "";
  });
}

// grab the inner row at `index`, reusing the existing array if it's
// already there (cleared) or creating a new one if we're extending past
// the end of `rowsOut`
function getOrCreateRow(rowsOut: HTMLElement[][], index: number): HTMLElement[] {
  const existing = rowsOut[index];

  if (existing) {
    existing.length = 0;

    return existing;
  }

  const fresh: HTMLElement[] = [];

  rowsOut[index] = fresh;

  return fresh;
}

// greedy pack items into rows using their natural widths — as many as
// will fit per row, then start a new row. `rowsOut` is mutated in place:
// its top-level length is reset and inner row arrays are reused where
// possible so repeated calls don't reallocate the row buckets.
function packRowsGreedily(
  items: HTMLElement[],
  containerWidth: number,
  gapWidth: number,
  rowsOut: HTMLElement[][],
): void {
  let rowIndex = 0;
  let current = getOrCreateRow(rowsOut, rowIndex);
  let currentWidth = 0;

  items.forEach((item) => {
    const itemWidth = item.clientWidth;
    const gapBefore = current.length > 0 ? gapWidth : 0;

    if (current.length > 0 && currentWidth + gapBefore + itemWidth > containerWidth) {
      rowIndex += 1;
      current = getOrCreateRow(rowsOut, rowIndex);
      currentWidth = 0;
    }

    currentWidth += current.length > 0 ? gapWidth + itemWidth : itemWidth;
    current.push(item);
  });

  // trim any rows left over from a previous, larger layout
  rowsOut.length = current.length > 0 ? rowIndex + 1 : rowIndex;
}

// redistribute items evenly across the row count already present in
// `rowsOut` so every row has as close to the same number of items as
// possible (differing by at most one). order is preserved. `rowsOut`
// is mutated in place and inner arrays are reused.
function redistributeRowsEvenly(items: HTMLElement[], rowsOut: HTMLElement[][]): void {
  const rowCount = Math.min(rowsOut.length, items.length);

  if (rowCount === 0) {
    rowsOut.length = 0;

    return;
  }

  const baseCount = Math.floor(items.length / rowCount);
  const remainder = items.length % rowCount;

  let cursor = 0;

  for (let r = 0; r < rowCount; r++) {
    const row = getOrCreateRow(rowsOut, r);
    // the first `remainder` rows absorb the extra item
    const size = r < remainder ? baseCount + 1 : baseCount;

    for (let i = 0; i < size; i++) {
      const item = items[cursor + i];

      if (item) row.push(item);
    }

    cursor += size;
  }

  rowsOut.length = rowCount;
}

// populate `rowDataOut` with one RowLayout per row, reusing existing
// RowLayout objects when available so repeated balance() calls don't
// allocate fresh objects.
function computeRowLayouts(
  rows: HTMLElement[][],
  containerWidth: number,
  gapWidth: number,
  baseHeight: number,
  minRowHeight: number,
  clampToMinHeight: boolean,
  rowDataOut: RowLayout[],
): void {
  rows.forEach((row, i) => {
    const totalNaturalWidth = row.reduce((sum, item) => sum + item.clientWidth, 0);
    const totalGap = (row.length - 1) * gapWidth;
    const available = containerWidth - totalGap - ROW_SAFETY_MARGIN_PX;
    const rawScale = available / totalNaturalWidth;
    // clamp at the minimum row height so lone oversized items don't drop
    // below natural size to fit the container — skipped in height-preserve
    // mode (heights are already pinned) and in aggressive mode (forced
    // redistribution may build rows that legitimately need to shrink)
    const scale = clampToMinHeight ? Math.max(rawScale, minRowHeight / baseHeight) : rawScale;
    const scaledHeight = baseHeight * scale;

    const existing = rowDataOut[i];

    if (existing) {
      existing.row = row;
      existing.totalNaturalWidth = totalNaturalWidth;
      existing.totalGap = totalGap;
      existing.scale = scale;
      existing.scaledHeight = scaledHeight;
    } else {
      rowDataOut[i] = {
        row,
        totalNaturalWidth,
        totalGap,
        scale,
        scaledHeight,
      };
    }
  });

  rowDataOut.length = rows.length;
}

// compute the shortest and tallest scaled heights across every row except
// the one at `ignoreIndex` — used to frame the last-row fill decision and
// to provide the fallback height for an unfilled last row.
function findScaledHeightExtrema(
  rowData: RowLayout[],
  ignoreIndex: number,
): { min: number; max: number } {
  let min = Infinity;
  let max = 0;

  rowData.forEach(({ scaledHeight }, i) => {
    if (i === ignoreIndex) return;

    if (scaledHeight < min) min = scaledHeight;
    if (scaledHeight > max) max = scaledHeight;
  });

  return { min, max };
}

// decide whether the last row should stretch to fill the container.
// aspect-ratio mode rejects fills that would make the last row more than
// 150% the tallest row above. height-preserve mode uses a natural-width
// fill threshold because row heights don't vary. aggressive mode always
// fills — redistribution has already made the last row dense.
function shouldLastRowFill(
  lastRow: RowLayout | undefined,
  maxOtherHeight: number,
  containerWidth: number,
  preserveHeight: boolean,
  aggressive: boolean,
): boolean {
  if (!lastRow) return true;
  if (aggressive) return true;

  if (preserveHeight) {
    const fillRatio = (lastRow.totalNaturalWidth + lastRow.totalGap) / containerWidth;

    return fillRatio >= LAST_ROW_FILL_THRESHOLD;
  }

  if (maxOtherHeight > 0) {
    return lastRow.scaledHeight <= maxOtherHeight * LAST_ROW_MAX_HEIGHT_RATIO;
  }

  return true;
}

function applyRowStyles(
  rowData: RowLayout[],
  lastRowShouldFill: boolean,
  fallbackHeight: number,
  preserveHeight: boolean,
  gapWidth: number,
): void {
  const lastIndex = rowData.length - 1;

  rowData.forEach(({ row, totalNaturalWidth, scaledHeight }, rowIndex) => {
    const isLastRow = rowIndex === lastIndex;
    const totalGap = (row.length - 1) * gapWidth;

    row.forEach((item) => {
      item.dataset.row = String(rowIndex); // for testing/debugging

      if (isLastRow && !lastRowShouldFill) {
        if (!preserveHeight && fallbackHeight !== Infinity) {
          item.style.height = `${fallbackHeight}px`;
        }

        return;
      }

      // Express widths as calc() percentages so they resolve against the
      // container's content box at paint time.  When a scrollbar appears
      // or disappears every item scales proportionally in the same frame
      // — no wrap, no reflow, no oscillation loop.
      //
      // Each item gets its natural-width fraction of the space left after
      // subtracting inter-item gaps and a 1px safety margin:
      //
      //   width = fraction * (100% - totalGap - safety)
      //
      // Because sum(fractions) = 1 the widths sum to exactly
      // 100% - totalGap - 1px, which together with flexbox-managed gaps
      // totals 100% - 1px — safely under 100%.
      const fraction = item.clientWidth / totalNaturalWidth;

      item.style.width = `calc(${fraction * 100}% - ${fraction * totalGap + fraction * ROW_SAFETY_MARGIN_PX}px)`;

      if (!preserveHeight) {
        item.style.height = `${scaledHeight}px`;
      }
    });
  });
}

const balanceRows: FunctionBasedModifier<BalanceRowsSignature> = modifier<BalanceRowsSignature>(
  (container, _positional, named) => {
    const preserve = named.preserve ?? "aspect-ratio";
    const aggressive = named.aggressive ?? false;
    const selector = named.selector ?? "li";
    let lastWidth = -1;
    let lastCount = -1;
    // rows and rowData are retained across balance() calls so their inner
    // arrays/objects can be reused instead of reallocated every time the
    // container resizes
    const rows: HTMLElement[][] = [];
    const rowData: RowLayout[] = [];

    // The modifier reads all of its geometry from the DOM, so it doesn't need
    // the data — it re-runs from these observations instead:
    // - childList: items added or removed
    // - the container's own style: --row-height / --gap (e.g. via @rowHeight)
    // - an item's style / width / height: a consumer resolving an image's
    //   aspect ratio, for instance
    // subtree reaches the items and their imgs. balance() disconnects while it
    // writes item styles, so our own writes never feed back in.
    const observe = () => {
      mutationObserver.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "width", "height"],
      });
    };

    const balance = () => {
      // Stop observing while we write, then reconnect from the settled DOM, so
      // the item widths and heights we set below can never retrigger us.
      mutationObserver.disconnect();

      try {
        // Only direct children: the items must be the container's own flex
        // children for the width math to hold, and scoping this way avoids
        // matching a selector that also appears in a consumer's item content
        // (e.g. a nested <li>). Exclude the sentinel too, since ":scope > div"
        // or "*" would otherwise match it.
        const listItems = Array.from(container.querySelectorAll(`:scope > ${selector}`)).filter(
          (element) => element !== sentinel,
        ) as HTMLElement[];
        const [firstItem] = listItems;

        if (!firstItem) return;

        const { containerWidth, gapWidth } = measureContainer(container);

        // ResizeObserver also fires when our own height changes ripple back
        // up to the container. Bail early if neither the width nor the item
        // count has changed so those reruns are cheap no-ops.
        if (containerWidth === lastWidth && listItems.length === lastCount) {
          return;
        }

        lastWidth = containerWidth;
        lastCount = listItems.length;

        resetItemDimensions(listItems);

        // read natural dimensions after reset — every item shares the same
        // base height set in CSS
        const baseHeight = firstItem.clientHeight;
        const minRowHeight = parseFloat(getComputedStyle(firstItem).height) || 0;

        // pack items into rows, then optionally rebalance so row sizes are
        // near-equal
        packRowsGreedily(listItems, containerWidth, gapWidth, rows);

        if (aggressive && rows.length >= 2) {
          redistributeRowsEvenly(listItems, rows);
        }

        // compute per-row layout (scale / scaledHeight).
        //
        // preserve: 'aspect-ratio' — the whole row grows or shrinks in
        //   lockstep, keeping every image's natural ratio and letting row
        //   heights vary between lines.
        // preserve: 'height' — row heights stay pinned to the CSS value and
        //   only widths stretch, so images get cropped via object-fit rather
        //   than the grid growing vertically.
        const preserveHeight = preserve === "height";
        const clampToMinHeight = !preserveHeight && !aggressive;

        computeRowLayouts(
          rows,
          containerWidth,
          gapWidth,
          baseHeight,
          minRowHeight,
          clampToMinHeight,
          rowData,
        );

        const lastIndex = rowData.length - 1;
        const { min: minScaledHeight, max: maxScaledHeight } = findScaledHeightExtrema(
          rowData,
          lastIndex,
        );

        const lastRowFill = shouldLastRowFill(
          rowData[lastIndex],
          maxScaledHeight,
          containerWidth,
          preserveHeight,
          aggressive,
        );

        applyRowStyles(rowData, lastRowFill, minScaledHeight, preserveHeight, gapWidth);
      } finally {
        observe();
      }
    };

    // Observing the container directly trips Chrome's "ResizeObserver
    // loop completed with undelivered notifications" warning: balance()
    // mutates item heights, which changes the flex container's block
    // size, which schedules another RO notification for the container,
    // and Chrome detects the loop even though our early-return at the
    // top of balance() makes that second notification a no-op. The
    // warning is benign but QUnit promotes it to a test failure.
    //
    // Fix: observe a zero-height absolutely-positioned sentinel instead.
    // Its inline-size tracks the container's padding box (width: 100%
    // on an abspos child), but its block-size is pinned to 0 and can't
    // be pushed around by sibling layout. So when balance() changes
    // item heights, the sentinel's box doesn't change, no further RO
    // notification is scheduled, and the delivery loop closes in one
    // pass. Reading width inside balance() still uses container
    // .clientWidth — the sentinel is purely a change-detection probe.
    //
    // The container needs `position: relative` for abspos anchoring;
    // set it inline so the modifier is self-contained and doesn't rely
    // on CSS it can't see.
    const originalPosition = container.style.position;

    if (getComputedStyle(container).position === "static") {
      container.style.position = "relative";
    }

    // A <div> inside a <ul> is technically invalid HTML, but since we
    // insert it via the DOM API (not through the HTML parser, which
    // would reparent it), browsers render it fine and it's invisible
    // to consumers that query by tag name (e.g. `ul > li` selectors in
    // tests). aria-hidden keeps it out of the accessibility tree.
    const sentinel = document.createElement("div");

    sentinel.setAttribute("aria-hidden", "true");
    sentinel.style.cssText =
      "position:absolute;top:0;left:0;width:100%;height:0;padding:0;margin:0;border:0;pointer-events:none;";

    // Install the sentinel before the MutationObserver so its insertion
    // doesn't fire a spurious mutation notification.
    container.appendChild(sentinel);

    const mutationObserver = new MutationObserver((records) => {
      if (sentinel.parentNode !== container) {
        container.appendChild(sentinel);
      }

      // An attribute change (a style / width / height on the container or an
      // item) moves neither the container width nor the item count, so the
      // width+count cache would skip the re-balance. Clear it to force a pass.
      if (records.some((record) => record.type === "attributes")) {
        lastWidth = -1;
      }

      balance();
    });

    const resizeObserver = new ResizeObserver(() => balance());

    // Run the initial balance synchronously from the modifier body so items
    // are correctly sized on first paint with no flicker. Its finally block
    // connects the mutation observer.
    balance();
    resizeObserver.observe(sentinel);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      sentinel.remove();
      container.style.position = originalPosition;
    };
  },
);

export default balanceRows;
